/**
 * FriendPickerSheet Component
 *
 * Unified multi-select friend picker for recommending menu items.
 * Used in both the check-in flow (deferred send) and activity/detail views (immediate send).
 * Includes search filtering and already-recommended detection.
 */

import { sentMenuItemRecommendationsQueryOptions } from '@/api/queries/friend-recommendation';
import { userFriendsQueryOptions } from '@/api/queries/friendship';
import { Modal } from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { TextInput } from '@/components/ui/text-input';
import { useAuth } from '@/hooks/use-auth';
import type { RecommendFriend } from '@/features/check-in/types';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';

const EMPTY_SELECTED: RecommendFriend[] = [];

interface FriendPickerSheetProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (selected: RecommendFriend[]) => void;
  /** When provided, fetches already-sent recommendations and disables those friends */
  menuItemId?: string;
  /** Pre-selected friends (for check-in flow where selection persists across opens) */
  initialSelected?: RecommendFriend[];
}

export function FriendPickerSheet({
  visible,
  onClose,
  onConfirm,
  menuItemId,
  initialSelected = EMPTY_SELECTED,
}: FriendPickerSheetProps) {
  const { user } = useAuth();

  const [selected, setSelected] = useState<Map<string, RecommendFriend>>(
    () => new Map(initialSelected.map((f) => [f.id, f]))
  );
  const [searchQuery, setSearchQuery] = useState('');
  // Track friend IDs confirmed this session (optimistic, survives sheet close/reopen)
  const [localConfirmedIds, setLocalConfirmedIds] = useState<Set<string>>(new Set());

  // Reset selection and search when sheet opens
  React.useEffect(() => {
    if (visible) {
      setSelected(new Map(initialSelected.map((f) => [f.id, f])));
      setSearchQuery('');
    }
  }, [visible, initialSelected]);

  const { data: friends, isLoading: isFriendsLoading } = useQuery({
    ...userFriendsQueryOptions(user?.id ?? ''),
    enabled: visible && !!user?.id,
  });

  const { data: sentRecommendations, isLoading: isSentLoading } = useQuery({
    ...sentMenuItemRecommendationsQueryOptions(menuItemId ?? ''),
    enabled: visible && !!menuItemId,
  });

  const isLoading = isFriendsLoading || (!!menuItemId && isSentLoading);

  // Build set of friend IDs that have already been recommended to
  // Merges server data with optimistic local confirmations
  const alreadySentIds = useMemo(() => {
    const ids = new Set<string>(localConfirmedIds);
    if (sentRecommendations?.recommendations) {
      for (const rec of sentRecommendations.recommendations) {
        ids.add(rec.recipient_id);
      }
    }
    return ids;
  }, [sentRecommendations, localConfirmedIds]);

  const filteredFriends = useMemo(() => {
    if (!friends) return [];
    if (!searchQuery.trim()) return friends;

    const query = searchQuery.trim().toLowerCase();
    return friends.filter((friend) => {
      const name = (friend.user.name || '').toLowerCase();
      const username = (friend.user.username || '').toLowerCase();
      return name.includes(query) || username.includes(query);
    });
  }, [friends, searchQuery]);

  const handleToggle = useCallback(
    (friendId: string, name: string, image: string | null) => {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(friendId)) {
          next.delete(friendId);
        } else {
          next.set(friendId, { id: friendId, name, image });
        }
        return next;
      });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    const confirmedFriends = Array.from(selected.values());
    onConfirm(confirmedFriends);
    // Optimistically mark these friends as sent so they show as
    // "Already recommended" if the sheet is reopened before the query refreshes
    if (confirmedFriends.length > 0) {
      setLocalConfirmedIds((prev) => {
        const next = new Set(prev);
        for (const f of confirmedFriends) {
          next.add(f.id);
        }
        return next;
      });
    }
    onClose();
  }, [selected, onConfirm, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal visible={visible} onClose={handleClose}>
      <View className="bg-background rounded-xl" style={{ maxHeight: 460 }}>
        <Modal.Header>
          <View className="flex-row items-center justify-between">
            <Text className="text-lg font-bold text-foreground">
              Recommend to Friends
            </Text>
            <Pressable
              onPress={handleClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Ionicons name="close" size={24} className="text-muted-foreground" />
            </Pressable>
          </View>
        </Modal.Header>

        {isLoading ? (
          <View className="py-12 items-center">
            <ActivityIndicator />
            <Text className="mt-2 text-sm text-muted-foreground">
              Loading friends...
            </Text>
          </View>
        ) : !friends || friends.length === 0 ? (
          <View className="items-center py-8 px-6">
            <Text className="text-sm text-muted-foreground">
              No friends to recommend to. Add friends first.
            </Text>
          </View>
        ) : (
          <>
            {/* Search bar -- pinned above scroll */}
            <View className="px-6 py-3">
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search friends..."
                size="sm"
                leftIcon={
                  <Ionicons name="search" size={16} className="text-muted-foreground" />
                }
                rightIcon={
                  searchQuery.length > 0 ? (
                    <Pressable onPress={() => setSearchQuery('')} hitSlop={8}>
                      <Ionicons name="close-circle" size={16} className="text-muted-foreground" />
                    </Pressable>
                  ) : undefined
                }
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>

            {filteredFriends.length === 0 ? (
              <View className="items-center py-8 px-6">
                <Text className="text-sm text-muted-foreground">
                  No friends matching &quot;{searchQuery}&quot;
                </Text>
              </View>
            ) : (
              <ScrollView
                style={{ height: 250 }}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingHorizontal: 24, paddingVertical: 8 }}
                keyboardShouldPersistTaps="handled"
              >
                {filteredFriends.map((friend) => {
                  const isAlreadySent = alreadySentIds.has(friend.friendId);
                  const isSelected = selected.has(friend.friendId);

                  return (
                    <Pressable
                      key={friend.id}
                      onPress={
                        isAlreadySent
                          ? undefined
                          : () =>
                              handleToggle(
                                friend.friendId,
                                friend.user.name || 'User',
                                friend.user.image ?? null
                              )
                      }
                      disabled={isAlreadySent}
                      className="flex-row items-center py-2.5"
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: isSelected, disabled: isAlreadySent }}
                      accessibilityLabel={friend.user.name || 'Friend'}
                      style={{ opacity: isAlreadySent ? 0.5 : 1 }}
                    >
                      {/* Checkbox or sent indicator */}
                      {isAlreadySent ? (
                        <Ionicons name="checkmark-circle" size={22} className="text-muted-foreground/40" />
                      ) : (
                        <Ionicons
                          name={isSelected ? 'checkmark-circle' : 'ellipse-outline'}
                          size={22}
                          className={isSelected ? 'text-primary' : 'text-muted-foreground/50'}
                        />
                      )}

                      {/* Avatar */}
                      <UserAvatar
                        uri={friend.user.image}
                        name={friend.user.name}
                        size={32}
                        decorative
                        style={{ marginLeft: 12 }}
                      />

                      {/* Name + username */}
                      <View className="flex-1 ml-2.5">
                        <Text
                          className={isAlreadySent ? 'text-sm text-muted-foreground' : 'text-sm text-foreground'}
                          numberOfLines={1}
                        >
                          {friend.user.name || 'User'}
                        </Text>
                        {friend.user.username && (
                          <Text
                            className="text-xs text-muted-foreground"
                            numberOfLines={1}
                          >
                            @{friend.user.username}
                          </Text>
                        )}
                      </View>

                      {/* Already sent label */}
                      {isAlreadySent && (
                        <Text className="text-xs text-muted-foreground/60">
                          Already recommended
                        </Text>
                      )}
                    </Pressable>
                  );
                })}
              </ScrollView>
            )}
          </>
        )}

        {/* Confirm footer */}
        {friends && friends.length > 0 && (
          <Modal.Footer>
            <Pressable
              onPress={handleClose}
              className="px-4 py-2 rounded-lg"
              accessibilityRole="button"
            >
              <Text className="text-sm text-muted-foreground">Cancel</Text>
            </Pressable>
            <Pressable
              onPress={handleConfirm}
              className="px-4 py-2 rounded-lg bg-primary"
              accessibilityRole="button"
              accessibilityLabel={`Confirm ${selected.size} friends`}
            >
              <Text className="text-sm font-medium text-primary-foreground">
                {selected.size > 0 ? `Confirm (${selected.size})` : 'Confirm'}
              </Text>
            </Pressable>
          </Modal.Footer>
        )}
      </View>
    </Modal>
  );
}
